// Weighted Google rating across a store's branches.
//
// WHY THIS EXISTS: `Store.googlePlaces` mirrors exactly ONE place — the primary branch, written by
// `syncPrimaryBranchToGooglePlaces` in the backend. For a chain that is structurally incapable of
// representing the business: the store header was showing Mohandseen's `4.5 · 53` as the Google
// rating of an 8-branch clinic. `Store.branches[]` is the real record, and nothing anywhere (front
// or back end) aggregated it. This does.
//
// WORKED EXAMPLE — `crystal-teeth`, the live 8-branch case, verified against production:
//
//   branch                         rating  reviews
//   Crystal Teeth                     2.7        9
//   Mohandseen (primary)              4.5       53
//   El Mokattam                       5.0        1
//   Dr. Ahmed Waleed                  4.9       38
//   Dr. Hatem ElZawahry               5.0        6
//   Porto Cairo                       4.0        7
//   Dr. Hatem Elzawahry (dup)         0          0   <- excluded
//   Zamalek                           0          0   <- excluded
//
//   2.7×9 + 4.5×53 + 5×1 + 4.9×38 + 5×6 + 4×7 = 512.0
//   512.0 / 114 = 4.4912…  ->  formatRating  ->  ٤٫٥      across 114 reviews, 6 of 8 branches
//
// The two guards below are BOTH load-bearing. Getting either wrong changes the answer:
//   naive mean, zeros included      -> 26.1 / 8  = 3.26   (wrong)
//   unweighted mean, zeros excluded -> 26.1 / 6  = 4.35   (wrong)
//   weighted, zeros excluded        -> 512 / 114 = 4.49   (correct)
//
// There is no frontend test runner in this repo, so the table above is the regression test.

export type BranchGoogleRatingInput = {
  googleRating?: number;
  googleReviewCount?: number;
};

export type BranchGoogleRatingAggregate = {
  /** Review-count-weighted mean of googleRating across rated branches, 0–5. NOT pre-rounded. */
  score: number;
  /** Sum of googleReviewCount across rated branches. */
  count: number;
  /** How many branches contributed to score/count. */
  ratedBranches: number;
  /** Every entry in the input array, rated or not — the store's real branch count. */
  totalBranches: number;
};

const isPositiveFinite = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

export function aggregateBranchGoogleRating(
  branches?: BranchGoogleRatingInput[] | null,
): BranchGoogleRatingAggregate | null {
  if (!Array.isArray(branches) || branches.length === 0) return null;

  let weightedSum = 0;
  let count = 0;
  let ratedBranches = 0;

  for (const branch of branches) {
    if (!branch) continue;

    const rating = branch.googleRating;
    const reviewCount = branch.googleReviewCount;

    // A rating without a count cannot be weighted, so it is excluded rather than silently given
    // weight 1. And Google has no 0-star rating: a 0 alongside a real count is a sync artifact,
    // not five 0-star reviews, so it must not drag the mean down.
    if (!isPositiveFinite(reviewCount)) continue;
    if (!isPositiveFinite(rating) || rating > 5) continue;

    weightedSum += rating * reviewCount;
    count += reviewCount;
    ratedBranches += 1;
  }

  if (ratedBranches === 0 || count === 0) return null;

  return {
    // Deliberately NOT rounded here — `formatRating` already does `toFixed(1)`. Rounding twice is
    // how off-by-0.1 bugs get in.
    score: weightedSum / count,
    count,
    ratedBranches,
    totalBranches: branches.length,
  };
}
